// src/pages/GamePlan.js
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftCircle } from 'lucide-react';

const GamePlan = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gameInfo, setGameInfo] = useState(null);

  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Get query params and game info from state
  const queryParams = new URLSearchParams(location.search);
  const abc = queryParams.get('abc');
  const xyz = queryParams.get('xyz');

  // Get game info from navigation state if available
  useEffect(() => {
    if (location.state?.game) {
      setGameInfo(location.state.game);
    }
  }, [location.state]);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      if (!gameId) {
        setError('Game ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `https://api.gamegems.co/web/games/plan-list/${gameId}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch game plans');
        }
        console.log(response);
        const data = await response.json();
        setPlans(data?.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [gameId]);

  const handleBackToGames = () => {
    navigate(`/game-list?abc=${abc}&xyz=${xyz}`);
  };

  return (
    <div className="flex bg-black min-h-screen">
      <div className="flex-1 flex flex-col">
        
        {/* Header Section */}
        <div className="bg-gray-900 shadow-lg border-b border-gray-700 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center">
              <button
                onClick={handleBackToGames}
                className="flex items-center text-gray-400 hover:text-white transition-colors mr-2"
                aria-label="Back to games"
              >
                <ArrowLeftCircle className="h-6 w-6 mr-2 flex-shrink-0" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-white">
                  Choose Your Plan
                </h1>
                {gameInfo && (
                  <p className="text-gray-300 mt-0 text-sm sm:text-base truncate">
                    for {gameInfo.title || gameInfo.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-3 sm:p-6">
          <div className="max-w-6xl mx-auto">
            {/* Game info card if available */}
            {gameInfo && (
              <div className="bg-gradient-to-r from-gray-800 to-gray-750 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-700 shadow-lg">
                <div className="flex items-center">
                  {gameInfo.image && (
                    <div className="relative flex-shrink-0">
                      <img
                        src={gameInfo.image.startsWith('http')
                          ? gameInfo.image
                          : `https://api.gamegems.co${gameInfo.image}`}
                        alt={gameInfo.title || gameInfo.name}
                        className="w-14 h-14 sm:w-20 sm:h-20 object-cover rounded-xl mr-4 sm:mr-6 border-2 border-gray-600"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-base sm:text-xl mb-1 sm:mb-2 truncate">
                      {gameInfo.title || gameInfo.name}
                    </h3>
                    <div className="flex items-center space-x-4">
                      <span className="px-2.5 sm:px-3 py-1 bg-blue-900/50 text-blue-300 text-xs sm:text-sm rounded-full border border-blue-700">
                        {gameInfo.genre || gameInfo.category || 'Game'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Plans content */}
            {loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-6"></div>
                <p className="text-gray-400 text-lg">Loading available plans...</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-8 max-w-md mx-auto">
                  <div className="text-red-400 text-4xl mb-4">⚠️</div>
                  <p className="text-red-300 mb-6 text-lg">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 max-w-md mx-auto">
                  <div className="text-gray-400 text-4xl mb-4">📋</div>
                  <p className="text-gray-400 text-lg">No plans available for this game.</p>
                  <button
                    onClick={handleBackToGames}
                    className="mt-4 px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Back to Games
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Plans Header */}
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-white mb-2">Available Plans</h2>
                  <p className="text-gray-400">Choose the plan that best fits your needs</p>
                </div>

                {/* Plans Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {plans.map((plan, index) => (
                    <div
                      key={plan.id}
                      className={`relative bg-gray-900 rounded-xl p-4 sm:p-6 border transition-all duration-300 sm:hover:scale-105 hover:shadow-xl ${
                        index === 1 ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      {/* Popular Badge */}
                      {index === 1 && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            POPULAR
                          </span>
                        </div>
                      )}

                      {/* Plan Header */}
                      <div className="text-center mb-6">
                        <div className="flex items-center justify-center mb-3">
                          <div>
                            <h3 className="text-white font-bold text-lg">
                              {plan.receiverCoin}
                            </h3>
                            <p className="text-gray-400 text-sm">
                              {plan.name || plan.title || 'Plan'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="bg-green-900/30 border border-green-700 rounded-lg p-3">
                          <span className="text-green-400 font-bold text-xl">
                            {plan.coin} Gems
                          </span>
                          <p className="text-green-300 text-xs mt-1">Required to purchase</p>
                        </div>
                      </div>

                      {/* Select Button */}
                      <button 
                        onClick={() => navigate(`/games/${gameId}?abc=${abc}&xyz=${xyz}`, { 
                          state: { game: gameInfo, selectedPlan: plan, abc, xyz } 
                        })}
                        className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                          index === 1 
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                            : 'bg-gradient text-white hover:opacity-90 shadow-md hover:shadow-lg'
                        }`}
                      >
                        <span className="flex items-center justify-center">
                          Select This Plan
                        </span>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Info Section */}
                {/* <div className="mt-8 bg-blue-900/20 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-start">
                    <span className="text-blue-400 mr-4 mt-1 text-xl">ℹ️</span>
                    <div>
                      <h4 className="text-blue-300 font-semibold mb-2">How it works</h4>
                      <ul className="text-blue-200 text-sm space-y-1">
                        <li>• Select your preferred plan above</li>
                        <li>• Enter your game account details</li>
                        <li>• Complete the purchase with your gems</li>
                        <li>• Receive your in-game currency instantly</li>
                      </ul>
                    </div>
                  </div>
                </div> */}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePlan;